import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { omit } from 'lodash';
import {
  AlertVariant,
  AlertStyleVariant,
  useAlert,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Alert';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useModal } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Modal';
import makeStyles from '@mui/styles/makeStyles';
import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { updateUserSetting } from '@monorepo/shared/src/api';
import BaseMatManModal from '@monorepo/shared/src/components/common/Modals/BaseMatManModal';
import BaseModalTitle from '@monorepo/shared/src/components/common/Modals/BaseModalTitle';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';

import { Character } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import ConfirmEditModal from 'components/modules/EditTeachingOrder/components/ConfirmEditModal';
import ConfirmOrderChangeModal from 'components/modules/EditTeachingOrder/components/ConfirmOrderChangeModal';
import {
  SwitchOrderAlert,
  SwitchOrderActionButton,
  CustomCreated,
  CustomEditedAndUpdated,
  CustomUpdated,
  CustomNameChanged,
  Error,
} from 'components/modules/EditTeachingOrder/components/EditTeachingOrderAlerts';
import RouteGuardModal from 'components/modules/EditTeachingOrder/components/RouteGuardModal';
import useTeachingOrderStore from 'graph/state/query-hooks/use-teaching-order';
import useUserStore from '@monorepo/shared/src/graph/hooks/use-user';
import { reducer, getInitialState } from './reducer';
import { State, Action, TypeName, TeachingOrdersArr, TeachingOrder } from './types';
import {
  formatCharacters,
  formatCharactersObjects,
  formatCharactersObjectById,
  formatUpdatedUserTeachingOrderSetting,
  determineIfActiveTeachingOrderDisplayed,
} from './util';
import { useRouteGuard } from 'state/hooks/use-route-guard';

const t = (key, options?) => translate('teachingOrder', key, options);

export const EditTeachingOrderContext = createContext<any>({});

const changeTeachingOrderError = 'changeTeachingOrderError';

const EditTeachingOrderProvider: React.FC<any> = ({ children }) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.TEACHING_ORDER_CHANGED);
  const [state, dispatch] = useReducer<React.Reducer<State, Action>>(reducer, getInitialState());
  const stateRef = useRef<State>(getInitialState());
  stateRef.current = state;
  const [, { openModal, closeModal }] = useModal();
  const [, { openAlert, closeAlert }] = useAlert();

  const navigate = useNavigate();

  const [user] = useUserStore();
  const userName = user?.me?.firstName;
  const userSettings = user?.me?.settings;
  const teachingOrderSetting = userSettings?.find((setting) => setting.setting === 'teachingOrder');

  const [
    { teachingOrders, activeTeachingOrder },
    {
      createCustomTeachingOrder,
      updateCustomTeachingOrder,
      customOrderRefetch,
      teachingOrderRefetch,
    },
  ] = useTeachingOrderStore();

  const {
    blockRouter,
    unblockRouter,
    isRouteGuardModalOpen,
    handleContinueEditing: continueRouteGuard,
  } = useRouteGuard();

  const classes = useStyles();

  // key that changes whenever the composition or order of teachingOrders changes,
  // so effects below don't miss reorders that a narrower dependency list would
  const teachingOrdersKey = teachingOrders.map((order) => order.id ?? order.name).join(',');

  // load data on mount
  useEffect(() => {
    setTeachingOrders(teachingOrders);
  }, [teachingOrdersKey, teachingOrderSetting]);

  const createTelemetryEvent = (nextActiveTeachingOrder) => {
    const isCustom = nextActiveTeachingOrder?.__typename === 'CustomTeachingOrder';
    fireEvent({
      teachingOrderId: nextActiveTeachingOrder?.id,
      custom: isCustom,
    });
  };

  const handleUpdateActiveTeachingOrder = async (
    activeTeachingOrderName: string,
    customOrder?: TeachingOrder
  ) => {
    try {
      const nextActiveTeachingOrder =
        customOrder ?? teachingOrders.find((order) => order.name === activeTeachingOrderName);
      const isCustomOrder = nextActiveTeachingOrder?.__typename === 'CustomTeachingOrder';
      const [license] = user.me.licenses;

      const updatedSettings = formatUpdatedUserTeachingOrderSetting(
        teachingOrderSetting?.value,
        nextActiveTeachingOrder,
        isCustomOrder,
        license.product.id
      );

      const response = await updateUserSetting('teachingOrder', {
        teachingOrder: updatedSettings,
      });

      if (response) {
        createTelemetryEvent(nextActiveTeachingOrder);
        await customOrderRefetch();
        await teachingOrderRefetch();
      }

      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const setHasUnsavedChanges = useCallback((hasUnsavedChanges) => {
    dispatch({
      type: 'SET_UNSAVED_CHANGES',
      payload: hasUnsavedChanges,
    });
  }, []);

  const setIsSaving = useCallback((isSaving: boolean) => {
    dispatch({ type: 'SET_IS_SAVING', payload: { isSaving } });
  }, []);

  const resetChanges = () => {
    const isReset = true;
    setTeachingOrders(teachingOrders, isReset);
    const activeTeachingOrderIndex = teachingOrders.indexOf(activeTeachingOrder);
    if (activeTeachingOrderIndex >= 0) {
      setActiveTeachingOrderIndex(activeTeachingOrderIndex);
    }
  };

  const setTeachingOrders = useCallback((teachingOrders: TeachingOrdersArr, isReset?: boolean) => {
    unblockRouter();
    const teachingOrdersMap = teachingOrders.reduce(
      (acc, current, index) => ({
        ...acc,
        [index]: { ...current, isInitialChange: isReset ? false : true },
      }),
      {}
    );
    dispatch({
      type: 'SET_TEACHING_ORDERS',
      payload: teachingOrdersMap,
    });
  }, []);

  const updateActiveTeachingOrder = useCallback(
    ({ characters, ...args }) => {
      dispatch({
        type: 'UPDATE_TEACHING_ORDER',
        payload: {
          [stateRef.current.activeTeachingOrderIndex]: {
            ...stateRef.current[stateRef.current.activeTeachingOrderIndex],
            characters:
              characters ?? stateRef.current[stateRef.current.activeTeachingOrderIndex].characters,
            isInitialChange: false,
            ...args,
          },
        },
      });
    },
    [state]
  );

  const setActiveTeachingOrderIndex = useCallback(
    (activeTeachingOrderIndex: number) => {
      dispatch({
        type: 'UPDATE_ACTIVE_INDEX',
        payload: {
          ...state,
          activeTeachingOrderIndex,
          previousActiveIndex: state.activeTeachingOrderIndex,
        },
      });
    },
    [state.activeTeachingOrderIndex]
  );

  const handleSetActiveTeachingOrder = (activeTeachingOrderIndex: number) => {
    setActiveTeachingOrderIndex(activeTeachingOrderIndex);
    closeModal();
    closeAlert();
  };

  const handleOpeningSuccessAlert = (body: any, iconType?: string, actionButton?: any) => {
    closeModal();
    openAlert({
      body,
      variant: AlertVariant.SUCCESS,
      styleVariant: AlertStyleVariant.OUTLINED,
      options: {
        duration: 4000,
        classes: {
          icon: classes.icon,
          root: classes.alertRoot,
        },
      },
      alertProps: {
        icon: iconType === 'info' ? <InfoIcon /> : <DoneIcon />,
      },
      action: actionButton,
    });
  };

  const handleOpeningErrorAlert = (errorMessage) => {
    closeModal();
    openAlert({
      body: <Error errorMessage={errorMessage} />,
      variant: AlertVariant.ERROR,
      styleVariant: AlertStyleVariant.OUTLINED,
      options: {
        duration: 4000,
        classes: {
          icon: classes.icon,
          root: classes.alertRootError,
        },
      },
      alertProps: {
        icon: <InfoIcon />,
      },
    });
  };

  const handleUndo = async () => {
    await handleSetActiveTeachingOrder(state.previousActiveIndex);
    const previousTeachingOrder = teachingOrders[state.previousActiveIndex];
    await handleUpdateActiveTeachingOrder(previousTeachingOrder?.name);
  };

  const handleConfirm = async (nextOrder) => {
    const response = await handleUpdateActiveTeachingOrder(nextOrder);

    if (!response) {
      await handleOpeningErrorAlert(changeTeachingOrderError);
      return;
    }
    await handleOpeningSuccessAlert(
      <SwitchOrderAlert name={nextOrder} />,
      'info',
      <SwitchOrderActionButton undoAction={handleUndo} />
    );
    await navigate('/home');
  };

  const confirmSettingTeachingOrder = () => {
    const { name: nextOrder } = state[state.activeTeachingOrderIndex];
    openModal({
      Title: () => <BaseModalTitle text={t('useThisOrder')} />,
      body: (
        <BaseMatManModal
          leftButtonAction={closeModal}
          rightButtonAction={() => handleConfirm(nextOrder)}
          headerText={`${nextOrder}`}
          leftButtonText={t('backToEditing')}
          rightButtonText={`${t('yes')}, ${t('useThisOrder')}`}
        >
          <ConfirmOrderChangeModal name={nextOrder} />
        </BaseMatManModal>
      ),
    });
  };

  const handleEditing = (characters: string[]) => {
    blockRouter();
    const formattedCharacters = formatCharactersObjects(
      characters,
      state[state.activeTeachingOrderIndex]?.characters
    );
    updateActiveTeachingOrder({ characters: formattedCharacters });
    setHasUnsavedChanges({ hasUnsavedChanges: true });
  };

  const handleCardChange = (characters: string[]) => {
    const { isInitialChange, __typename: teachingOrderType } =
      state[state.activeTeachingOrderIndex];

    if (isInitialChange && teachingOrderType !== TypeName.CUSTOM_TEACHING_ORDER) {
      displayChangeConfirmationModal(characters);
      return;
    }

    handleEditing(characters);
  };

  const handleRevertOrderChanges = () => {
    unblockRouter();
    setTeachingOrders(teachingOrders, true);
    const currentlySelectedTeachingOrder = teachingOrders[state.activeTeachingOrderIndex];
    updateActiveTeachingOrder({ characters: currentlySelectedTeachingOrder?.characters });
    // activeCharacters can end up value/reference-identical to before the drag,
    // so bump an explicit signal consumers can rely on to force their own resync
    dispatch({ type: 'BUMP_RESET_SIGNAL' });
    closeModal();
    setHasUnsavedChanges({ hasUnsavedChanges: false });
  };

  const handleContinueEditing = (characters: string[]) => {
    continueRouteGuard();
    closeModal();
    handleEditing(characters);
  };

  const setCustomTeachingOrder = (characters: Character[]) => {
    setActiveTeachingOrderIndex(0); // custom teaching order is always first in the array
    const formattedCharacters = formatCharactersObjectById(
      characters,
      stateRef.current[stateRef.current.activeTeachingOrderIndex]?.characters
    );
    updateActiveTeachingOrder({ characters: formattedCharacters });
  };

  const resetTeachingOrder = (teachingOrderIndex: number) => {
    const teachingOrder = teachingOrders[teachingOrderIndex];
    dispatch({
      type: 'UPDATE_TEACHING_ORDER',
      payload: {
        [teachingOrderIndex]: {
          ...stateRef.current[teachingOrderIndex],
          characters: teachingOrder?.characters,
          isInitialChange: false,
        },
      },
    });
  };

  const handleUpdatingCustomTeachingOrder = async (id, name, formattedCharacters) => {
    await updateCustomTeachingOrder({ id, name, characterIds: formattedCharacters });
    await handleOpeningSuccessAlert(<CustomEditedAndUpdated name={name} />, 'info', <div />);
    setIsSaving(false);
    navigate('/home');
  };

  const handleUpdatingCustomerTeachingOrderAndRedirect = async (
    formattedCharacters,
    customOrder,
    newName,
    teachingOrderIndex
  ) => {
    await updateCustomTeachingOrder({
      id: customOrder.id,
      name: customOrder.name,
      characterIds: formattedCharacters,
    });
    navigate('/home');

    const response = await handleUpdateActiveTeachingOrder(customOrder.name);
    if (!response) {
      await handleOpeningErrorAlert(changeTeachingOrderError);
      return;
    }
    resetTeachingOrder(teachingOrderIndex);
    setCustomTeachingOrder(formattedCharacters);
    handleOpeningSuccessAlert(<CustomUpdated name={newName} />, 'info', <div />);
    setIsSaving(false);
  };

  const handleCreateCustomOrder = async (formattedCharacters, newName, teachingOrderIndex) => {
    const customOrder = await createCustomTeachingOrder(formattedCharacters);
    const response = await handleUpdateActiveTeachingOrder(newName, customOrder);
    if (!response || !customOrder) {
      await handleOpeningErrorAlert(changeTeachingOrderError);
      return;
    }
    resetTeachingOrder(teachingOrderIndex);
    await setCustomTeachingOrder(formattedCharacters);
    handleOpeningSuccessAlert(<CustomCreated name={newName} />, 'success', <div />);
    setIsSaving(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    const teachingOrderIndex = state.activeTeachingOrderIndex;
    const { id, name, characters, __typename: orderType } = state[teachingOrderIndex];
    const formattedCharacters = formatCharacters(characters || []);

    const customOrder = teachingOrders?.find(
      (teachingOrder) => teachingOrder.__typename === TypeName.CUSTOM_TEACHING_ORDER
    );

    const newName = `${userName}'s Classroom Order`;

    if (orderType === TypeName.CUSTOM_TEACHING_ORDER) {
      handleUpdatingCustomTeachingOrder(id, name, formattedCharacters);
    } else if (customOrder) {
      handleUpdatingCustomerTeachingOrderAndRedirect(
        formattedCharacters,
        customOrder,
        newName,
        teachingOrderIndex
      );
    } else {
      handleCreateCustomOrder(formattedCharacters, newName, teachingOrderIndex);
    }
    setHasUnsavedChanges({ hasUnsavedChanges: false });
    unblockRouter();
  };

  const handleUpdateCustomOrderName = async (id, newName) => {
    await updateCustomTeachingOrder({ id, name: newName });
    updateActiveTeachingOrder({ name: newName });
    await handleOpeningSuccessAlert(<CustomNameChanged name={newName} />, 'success', <div />);
  };

  const handleSaveName = useCallback(
    (newName) => {
      const { id, __typename: orderType } = state[state.activeTeachingOrderIndex];

      if (orderType === TypeName.CUSTOM_TEACHING_ORDER) {
        return handleUpdateCustomOrderName(id, newName);
      }
    },
    [state[state.activeTeachingOrderIndex], user]
  );

  const displayChangeConfirmationModal = (characters: string[]) => {
    const { name } = state[state.activeTeachingOrderIndex];
    openModal({
      Title: () => <BaseModalTitle text={t('yourTeachingOrder')} />,
      body: (
        <BaseMatManModal
          leftButtonAction={handleRevertOrderChanges}
          rightButtonAction={() => handleContinueEditing(characters)}
          headerText={`${t('changesTo', { name })}`}
          leftButtonText={`${t('backTo', { name })}`}
          rightButtonText={t('continueEditing')}
        >
          <ConfirmEditModal name={name} userName={userName} />
        </BaseMatManModal>
      ),
    });
  };

  const handleBackToEditing = () => {
    closeModal();
    continueRouteGuard();
  };

  const handleSaveChanges = () => {
    handleSave();
    unblockRouter();
  };

  const handleExiting = () => {
    unblockRouter();
    resetChanges();
    navigate(-1);
  };

  const requestForExitingEditMode = () => {
    if (state.hasUnsavedChanges) {
      displayRouteGuardModal();
    } else handleExiting();
  };

  const displayRouteGuardModal = () => {
    openModal({
      Title: () => <BaseModalTitle text={t('unsavedChanges')} onClose={handleBackToEditing} />,
      body: (
        <BaseMatManModal
          leftButtonAction={handleBackToEditing}
          rightButtonAction={handleSaveChanges}
          headerText={t('unsavedChangesHeader')}
          headerFontSize={'24px'}
          leftButtonText={t('backToEditing')}
          rightButtonText={t('saveChanges')}
        >
          <RouteGuardModal />
        </BaseMatManModal>
      ),
    });
  };

  const serializedTeachingOrders: any = useMemo(
    () =>
      Object.values(
        omit(state, [
          'teachingOrder',
          'isInitialChange',
          'activeCharacters',
          'activeTeachingOrderIndex',
          'selectedChar',
          'hasUnsavedChanges',
          'activeTeachingOrder',
          'previousActiveIndex',
          'isActiveTeachingOrderDisplayed',
          'resetSignal',
          'isSaving',
        ])
      ),
    [state]
  );

  useEffect(() => {
    if (isRouteGuardModalOpen) {
      setTimeout(() => {
        displayRouteGuardModal();
      }, 300);
    }
  }, [isRouteGuardModalOpen]);

  useEffect(() => {
    const nextActiveTeachingOrderIndex = teachingOrders.indexOf(activeTeachingOrder);
    if (nextActiveTeachingOrderIndex !== -1) {
      setActiveTeachingOrderIndex(nextActiveTeachingOrderIndex);
    }
  }, [activeTeachingOrder, teachingOrdersKey]);

  const isActiveTeachingOrderDisplayed = determineIfActiveTeachingOrderDisplayed(
    activeTeachingOrder,
    teachingOrders,
    state.activeTeachingOrderIndex
  );

  return (
    <EditTeachingOrderContext.Provider
      value={[
        {
          ...state,
          activeCharacters: state[state.activeTeachingOrderIndex]?.characters,
          serializedTeachingOrders,
          activeTeachingOrder: teachingOrders[state.activeTeachingOrderIndex],
          isActiveTeachingOrderDisplayed,
        },
        {
          setTeachingOrders,
          updateActiveTeachingOrder,
          setActiveTeachingOrderIndex,
          confirmSettingTeachingOrder,
          handleSetActiveTeachingOrder,
          handleCardChange,
          setIsSaving,
          handleSave,
          handleSaveName,
          resetChanges,
          handleExiting,
          requestForExitingEditMode,
        },
      ]}
    >
      {children}
    </EditTeachingOrderContext.Provider>
  );
};

const useStyles = makeStyles((theme: any) => ({
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertRoot: {
    backgroundColor: theme.palette.success.lighter,
    color: theme.palette.black.main,
  },
  alertRootError: {
    backgroundColor: theme.palette.error.lightest,
    color: theme.palette.black.main,
  },
}));

export default EditTeachingOrderProvider;
